// ============================================================================
// BULK CALCULATOR
// ============================================================================
// Spreadsheet-like bulk emissions calculator with session management
// ============================================================================

import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { emissionsApi } from '../api/emissions.api';
import PageInfoButton from '@/components/PageInfoButton';

interface BulkRow {
  id?: number;
  row_number: number;
  activity_description: string;
  emission_factor_id?: number;
  activity_amount: number;
  input_unit: string;
  scope?: string;
  scope3_category?: string;
  notes?: string;
  co2e_kg?: number;
  error_message?: string;
}

interface Session {
  id: number;
  session_name: string;
  company_id: number;
  gwp_standard?: string;
  scope2_method?: string;
  status?: string;
  total_co2e?: string | number;
  total_co2e_kg?: number;
  row_count?: number;
  rows?: BulkRow[];
}

interface NewSessionForm {
  session_name: string;
  gwp_standard: string;
  scope2_method: string;
}

const DEFAULT_FORM: NewSessionForm = {
  session_name: '',
  gwp_standard: 'AR5',
  scope2_method: 'location',
};

const SCOPES = ['Scope 1', 'Scope 2', 'Scope 3'];

const BulkCalculator: React.FC = () => {
  const { user } = useAuth();
  const companyId: number = (user as any)?.company_id ?? 0;

  const [sessions, setSessions] = useState<Session[]>([]);
  const [activeSession, setActiveSession] = useState<Session | null>(null);
  const [systemFactors, setSystemFactors] = useState<any[]>([]);
  const [newSessionForm, setNewSessionForm] = useState<NewSessionForm>(DEFAULT_FORM);
  const [showNewSession, setShowNewSession] = useState(false);
  const [localRows, setLocalRows] = useState<BulkRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSessions = useCallback(async () => {
    try {
      const data = await emissionsApi.getBulkSessions();
      setSessions(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load sessions');
    }
  }, []);

  const loadSystemFactors = useCallback(async () => {
    try {
      const data = await emissionsApi.getSystemFactors();
      setSystemFactors(Array.isArray(data) ? data : []);
    } catch {
      // non-critical
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadSessions(), loadSystemFactors()]);
      setLoading(false);
    };
    init();
  }, [loadSessions, loadSystemFactors]);

  const openSession = async (session: Session) => {
    try {
      const full = await emissionsApi.getBulkSession(session.id);
      setActiveSession(full);
      setLocalRows(full?.rows ?? []);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load session');
    }
  };

  const createSession = async () => {
    if (!newSessionForm.session_name.trim()) return;
    try {
      const created = await emissionsApi.createBulkSession({
        company_id: companyId,
        session_name: newSessionForm.session_name,
        gwp_standard: newSessionForm.gwp_standard,
        scope2_method: newSessionForm.scope2_method,
      });
      setShowNewSession(false);
      setNewSessionForm(DEFAULT_FORM);
      await loadSessions();
      if (created?.id) {
        await openSession(created);
      }
    } catch (err: any) {
      setError(err?.message ?? 'Failed to create session');
    }
  };

  const deleteSession = async (id: number) => {
    try {
      await emissionsApi.deleteBulkSession(id);
      if (activeSession?.id === id) {
        setActiveSession(null);
        setLocalRows([]);
      }
      await loadSessions();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to delete session');
    }
  };

  const addRow = () => {
    const next: BulkRow = {
      row_number: localRows.length + 1,
      activity_description: '',
      activity_amount: 0,
      input_unit: 'kg',
      scope: 'Scope 1',
    };
    setLocalRows(prev => [...prev, next]);
  };

  const updateRow = (index: number, field: keyof BulkRow, value: any) => {
    setLocalRows(prev => {
      const updated = [...prev];
      const row = { ...updated[index] } as BulkRow;
      (row as any)[field] = value;
      updated[index] = row;
      return updated;
    });
  };

  const calculateAll = async () => {
    if (!activeSession) return;
    try {
      setProcessing(true);
      setError(null);
      // Push all local rows that don't have server IDs
      for (let i = 0; i < localRows.length; i++) {
        const row = localRows[i];
        if (!row) continue;
        if (!row.id) {
          const payload: Parameters<typeof emissionsApi.addBulkRow>[1] = {
            row_number: row.row_number,
            activity_description: row.activity_description,
            activity_amount: row.activity_amount,
            input_unit: row.input_unit,
          };
          if (row.emission_factor_id !== undefined) payload.emission_factor_id = row.emission_factor_id;
          if (row.scope) payload.scope = row.scope;
          if (row.scope3_category) payload.scope3_category = row.scope3_category;
          if (row.notes) payload.notes = row.notes;
          await emissionsApi.addBulkRow(activeSession.id, payload);
        }
      }
      const result = await emissionsApi.processBulkSession(activeSession.id);
      const full = await emissionsApi.getBulkSession(activeSession.id);
      setActiveSession(full);
      setLocalRows(full?.rows ?? []);
      await loadSessions();
      if (result) {
        // result may contain summary
      }
    } catch (err: any) {
      setError(err?.message ?? 'Calculation failed');
    } finally {
      setProcessing(false);
    }
  };

  const totalCo2e = localRows.reduce((sum, r) => sum + (Number(r.co2e_kg) || 0), 0);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-emerald-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Bulk Calculator</h1><PageInfoButton /></div>
          <p className="text-slate-500 mt-1">Create sessions and calculate emissions in bulk</p>
        </div>
        <button
          onClick={() => setShowNewSession(true)}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
        >
          + New Session
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="flex gap-6">
        {/* Session List */}
        <div className="w-64 shrink-0">
          <div className="bg-white border rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b bg-slate-50">
              <h2 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Sessions</h2>
            </div>
            {sessions.length === 0 ? (
              <div className="px-4 py-6 text-center text-slate-400 text-sm">No sessions yet</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {sessions.map((s: Session) => (
                  <li key={s.id}>
                    <button
                      onClick={() => openSession(s)}
                      className={`w-full text-left px-4 py-3 text-sm hover:bg-slate-50 transition-colors ${
                        activeSession?.id === s.id ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-slate-700'
                      }`}
                    >
                      <div className="font-medium truncate">{s.session_name}</div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        {Number(s.total_co2e ?? 0) > 0
                          ? `${(Number(s.total_co2e) * 1000).toLocaleString(undefined, { maximumFractionDigits: 2 })} kg CO₂e`
                          : s.status === 'completed'
                            ? (s.row_count ? `${s.row_count} rows — check factors (0 CO₂e)` : 'No data rows')
                            : s.status === 'failed'
                              ? 'Calculation failed'
                              : s.row_count ? `${s.row_count} rows · ${s.status || 'draft'}` : (s.status || 'draft')
                        }
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Active Session View */}
        <div className="flex-1 min-w-0">
          {!activeSession ? (
            <div className="bg-white border rounded-xl p-12 text-slate-400">
              <p className="text-lg font-medium mb-2">No session selected</p>
              <p className="text-sm">Create a new session or select one from the list</p>
            </div>
          ) : (
            <div className="bg-white border rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-slate-800">{activeSession.session_name}</h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    GWP: {activeSession.gwp_standard ?? 'AR5'} · Scope 2: {activeSession.scope2_method ?? 'location'}
                    {activeSession.status && ` · Status: ${activeSession.status}`}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {totalCo2e > 0 && (
                    <div className="text-right">
                      <div className="text-xs text-slate-500">Total CO₂e</div>
                      <div className="text-sm font-bold text-emerald-700">
                        {totalCo2e.toLocaleString(undefined, { maximumFractionDigits: 2 })} kg
                      </div>
                    </div>
                  )}
                  <button
                    onClick={addRow}
                    className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    + Add Row
                  </button>
                  <button
                    onClick={calculateAll}
                    disabled={processing || localRows.length === 0}
                    className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                  >
                    {processing ? 'Calculating…' : 'Calculate All'}
                  </button>
                  <button
                    onClick={() => deleteSession(activeSession.id)}
                    className="px-3 py-1.5 border border-red-200 text-red-600 rounded-lg text-sm hover:bg-red-50 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      <th className="px-3 py-3 text-left w-10">#</th>
                      <th className="px-3 py-3 text-left min-w-48">Description</th>
                      <th className="px-3 py-3 text-left min-w-44">Emission Factor</th>
                      <th className="px-3 py-3 text-right w-28">Amount</th>
                      <th className="px-3 py-3 text-left w-24">Unit</th>
                      <th className="px-3 py-3 text-left w-28">Scope</th>
                      <th className="px-3 py-3 text-right w-32">CO₂e (kg)</th>
                      <th className="px-3 py-3 text-left">Error</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {localRows.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                          No rows yet. Click "+ Add Row" to begin.
                        </td>
                      </tr>
                    ) : (
                      localRows.map((row, i) => (
                        <tr key={i} className="hover:bg-slate-50">
                          <td className="px-3 py-2 text-left text-slate-400 text-xs">{row.row_number}</td>
                          <td className="px-3 py-2 text-left">
                            <input
                              type="text"
                              value={row.activity_description}
                              onChange={e => updateRow(i, 'activity_description', e.target.value)}
                              placeholder="Activity description"
                              className="w-full border-0 bg-transparent focus:outline-none focus:ring-1 focus:ring-emerald-400 rounded px-1 py-0.5 text-sm"
                            />
                          </td>
                          <td className="px-3 py-2 text-left">
                            <select
                              value={row.emission_factor_id ?? ''}
                              onChange={e => updateRow(i, 'emission_factor_id', e.target.value ? Number(e.target.value) : undefined)}
                              className="w-full border-0 bg-transparent focus:outline-none focus:ring-1 focus:ring-emerald-400 rounded px-1 py-0.5 text-sm"
                            >
                              <option value="">— Select —</option>
                              {systemFactors.map((f: any) => (
                                <option key={f.id} value={f.id}>{f.name ?? f.factor_name}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2 text-left">
                            <input
                              type="number"
                              min="0"
                              step="any"
                              value={row.activity_amount}
                              onChange={e => updateRow(i, 'activity_amount', Number(e.target.value))}
                              className="w-full border-0 bg-transparent focus:outline-none focus:ring-1 focus:ring-emerald-400 rounded px-1 py-0.5 text-sm text-right"
                            />
                          </td>
                          <td className="px-3 py-2 text-left">
                            <input
                              type="text"
                              value={row.input_unit}
                              onChange={e => updateRow(i, 'input_unit', e.target.value)}
                              placeholder="kg"
                              className="w-full border-0 bg-transparent focus:outline-none focus:ring-1 focus:ring-emerald-400 rounded px-1 py-0.5 text-sm"
                            />
                          </td>
                          <td className="px-3 py-2 text-left">
                            <select
                              value={row.scope ?? ''}
                              onChange={e => updateRow(i, 'scope', e.target.value)}
                              className="w-full border-0 bg-transparent focus:outline-none focus:ring-1 focus:ring-emerald-400 rounded px-1 py-0.5 text-sm"
                            >
                              <option value="">—</option>
                              {SCOPES.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </td>
                          <td className="px-3 py-2 text-left font-semibold text-emerald-700">
                            {row.co2e_kg != null ? Number(row.co2e_kg).toLocaleString(undefined, { maximumFractionDigits: 4 }) : '—'}
                          </td>
                          <td className="px-3 py-2 text-left text-red-500 text-xs truncate max-w-32">
                            {row.error_message ?? ''}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Session Modal */}
      {showNewSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md mx-4">
            <h2 className="text-lg font-bold text-slate-900 mb-4">New Bulk Session</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Session Name *</label>
                <input
                  type="text"
                  value={newSessionForm.session_name}
                  onChange={e => setNewSessionForm(f => ({ ...f, session_name: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="e.g. Q1 2025 Calculations"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">GWP Standard</label>
                <select
                  value={newSessionForm.gwp_standard}
                  onChange={e => setNewSessionForm(f => ({ ...f, gwp_standard: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="AR4">AR4</option>
                  <option value="AR5">AR5</option>
                  <option value="AR6">AR6</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Scope 2 Method</label>
                <select
                  value={newSessionForm.scope2_method}
                  onChange={e => setNewSessionForm(f => ({ ...f, scope2_method: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="location">Location-based</option>
                  <option value="market">Market-based</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowNewSession(false); setNewSessionForm(DEFAULT_FORM); }}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={createSession}
                disabled={!newSessionForm.session_name.trim()}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              >
                Create Session
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkCalculator;
